import { useNavigate } from "react-router-dom";
import { useLocationHistoryStack } from "./locationHistoryStack";

interface UseEntityNavigationParams {
  basePath: string;
  pathBack?: string;
  onBack?: () => void;
}

/**
 * Navegação padrão das páginas de entidade — listar, criar, editar e voltar
 *
 * O goBack usa o histórico para não devolver o usuário ao formulário de criação:
 * se a tela anterior era o "novo", volta para a listagem; caso contrário anda um
 * passo atrás no histórico do navegador. Com onBack, nada disso acontece.
 *
 * @param basePath - Caminho da listagem, ex.: "/towermanagement/origins"
 * @param pathBack - Destino alternativo do goBack
 * @param onBack - Tem prioridade sobre a navegação do goBack
 * @returns Os dois caminhos montados e os três navegadores da página
 */
export const useEntityNavigation = ({
  basePath,
  pathBack: pathBackParam,
  onBack,
}: UseEntityNavigationParams) => {
  const navigate = useNavigate();
  const { previous, current } = useLocationHistoryStack();

  const pathNew = `${basePath}/new`;
  const pathBack = pathBackParam ?? basePath;

  const goAdd = () => {
    navigate(pathNew);
  };

  const goEdit = (id: string | number) => {
    navigate(`${basePath}/${id}`);
  };

  const goBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (previous?.pathname.includes(pathNew)) {
      navigate(pathBack);
      return;
    }
    
    if (current && previous?.pathname.startsWith(`${current.pathname}/`)) {
      navigate(pathBack);
      return;
    }
    navigate(-1);
  };

  return { pathNew, pathBack, goAdd, goEdit, goBack };
};
